import { Tooltip, Switch, Label, Typography } from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { Alert, toast } from "@altimateai/lego";
import useMCPContext from "@/pages/mcp/useMCPContext";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { editDatamate } from "@/helpers/apis";
import { TeammatesApplicableFor } from "@/pages/mcp/constants";

interface Props {
  showInfo?: boolean;
}

const InfoText =
  "Enable intelligent knowledge retrieval and contextual understanding across your data sources —transforming scattered information into actionable insights.";

const KnowledgeEngineToggle = ({ showInfo }: Props) => {
  const { teammate, updateTeammate } = useMCPContext();
  const queryClient = useQueryClient();

  // mutation for updating teammate
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (isEnabled: boolean) => {
      if (teammate?.id) {
        return editDatamate(teammate.id, { ...teammate, knowledge_engine_enabled: isEnabled });
      }
      updateTeammate({ knowledge_engine_enabled: isEnabled });
    },
    onSuccess: () => {
      if (teammate?.id) {
        queryClient.invalidateQueries({ queryKey: [`FILTER_${TeammatesApplicableFor}`] });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: "Could not update datamate",
        description: (error as Error).message,
        variant: "destructive",
      });
    },
  });

  return (
    <Stack direction="column" align="start" justify="start">
      <div className="al-p-0">
        <Tooltip
          title={
            teammate?.knowledge_engine_enabled
              ? "Knowledge Engine is enabled"
              : "Knowledge Engine is disabled"
          }
        >
          <Stack direction="row" align="center">
            <Label htmlFor="knowledge-engine-toggle" className="al-m-0">
              Knowledge Engine
            </Label>
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <Switch
                id="knowledge-engine-toggle"
                checked={teammate?.knowledge_engine_enabled || false}
                onCheckedChange={(checked: boolean) => mutate(checked)}
                className="!al-ml-0"
              />
            )}
          </Stack>
        </Tooltip>
      </div>
      <Typography variant="body" className="al-text-gray-600 al-text-sm al-mt-1">
        Enable this to extract and retrieve relevant knowledge automatically in IDE
      </Typography>
      {showInfo && <Alert>{InfoText}</Alert>}
    </Stack>
  );
};

export default KnowledgeEngineToggle;
